import { useCallback, useEffect, useState } from 'react';
import {
  MEMBERSHIP_LEVELS,
  MEMBERSHIP_LEVEL_DESCRIPTIONS,
  MEMBERSHIP_LEVEL_LABELS,
  type MembershipLevel,
} from '@ni/shared';
import { api, errorLine } from '../../api/client';
import type { AdminMember, AdminOrg } from '../../api/types';
import { Select } from '../../components/ui/select';
import { formatDay, joinClauses } from '../../lib/format';

/**
 * The picker's options, in the order the levels are declared — most access
 * first, which is the order somebody reads a list of seats in.
 */
const LEVEL_OPTIONS = MEMBERSHIP_LEVELS.map((level) => ({
  value: level,
  label: MEMBERSHIP_LEVEL_LABELS[level],
}));

/**
 * **The roster** — who can reach an organization, and the two writes on it: an
 * invite that pre-provisions an account, and a revoke that removes a membership
 * row and nothing else.
 */

/**
 * The roster, as a component of its own — `null` is "still loading", `[]` is an
 * org nobody has been given access to yet.
 *
 * Split out of {@link Members} so it can be rendered from a fixture: the copy
 * sweep (`lib/copy.spec.tsx`) has to see the states a page driven only by
 * effects never reaches under `renderToStaticMarkup`, and an empty state is
 * exactly where a stale word survives longest.
 */
export function MembersTable({
  members,
  working,
  onRevoke,
  onLevel,
}: {
  members: AdminMember[] | null;
  /** The user id a write is in flight for, if any — Revoke is disabled while one is. */
  working: string | null;
  onRevoke: (member: AdminMember) => void;
  onLevel: (member: AdminMember, level: MembershipLevel) => void;
}) {
  if (members === null) return <div className="empty-line">Loading.</div>;
  if (members.length === 0)
    return <div className="empty-line">Nobody has access to this organization yet.</div>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Person</th>
          <th>Access</th>
          <th>Added</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {members.map((member) => (
          <tr key={member.membership_id}>
            <td>
              {member.name ?? member.email}
              <div className="sub">
                {joinClauses([
                  member.name ? member.email : null,
                  member.linked ? 'has signed in' : 'invited, not signed in yet',
                ])}
              </div>
            </td>
            {/* The seat is changed where it is read. A separate edit screen for
                one field would be a screen somebody has to go and find, and the
                roster is the place the question "who can do what here" is asked. */}
            <td>
              <Select
                id={`level-${member.user_id}`}
                value={member.level}
                options={LEVEL_OPTIONS}
                disabled={working !== null}
                onChange={(level) => onLevel(member, level as MembershipLevel)}
              />
              <div className="sub">{MEMBERSHIP_LEVEL_DESCRIPTIONS[member.level]}</div>
            </td>
            <td className="quiet-cell">{formatDay(member.created_at)}</td>
            <td className="actions">
              <button
                type="button"
                className="btn act tiny"
                disabled={working !== null}
                onClick={() => onRevoke(member)}
              >
                {working === member.user_id ? 'Revoking…' : 'Revoke'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Members({ org }: { org: AdminOrg }) {
  const [members, setMembers] = useState<AdminMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  /**
   * **The invite starts on the most limited seat.** The route requires the field
   * and will not default it; a form that opened on Owner would hand the default
   * back to whoever clicks through fastest, which is the thing requiring it was
   * meant to stop.
   */
  const [level, setLevel] = useState<MembershipLevel>('read_only');
  /** The user id a write is in flight for, or `'invite'` — one at a time. */
  const [working, setWorking] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMembers((await api.listMembers(org.id)).members);
      setError(null);
    } catch (caught) {
      setError(errorLine(caught));
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async () => {
    const address = email.trim();
    if (address.length === 0 || working) return;
    setWorking('invite');
    try {
      await api.addMember(org.id, {
        email: address,
        ...(name.trim().length > 0 ? { name: name.trim() } : {}),
        level,
      });
      setEmail('');
      setName('');
      setLevel('read_only');
      await load();
      setError(null);
    } catch (caught) {
      setError(errorLine(caught));
    } finally {
      setWorking(null);
    }
  };

  const changeLevel = async (member: AdminMember, next: MembershipLevel) => {
    if (working || next === member.level) return;
    setWorking(member.user_id);
    try {
      await api.setMemberLevel(org.id, member.user_id, next);
      await load();
      setError(null);
    } catch (caught) {
      setError(errorLine(caught));
    } finally {
      setWorking(null);
    }
  };

  const revoke = async (member: AdminMember) => {
    if (working) return;
    setWorking(member.user_id);
    try {
      await api.removeMember(org.id, member.user_id);
      await load();
      setError(null);
    } catch (caught) {
      setError(errorLine(caught));
    } finally {
      setWorking(null);
    }
  };

  return (
    /* No heading. It said "Members of {org.name}" while this was one panel among
       three on a page that never named the org; now the page's own title is the
       org and the tab above is the word Members, so a heading here would be the
       third time in four inches. */
    <div className="section">
      {error ? (
        <div className="flag row">
          <div className="label">Error</div>
          <div className="detail">{error}</div>
        </div>
      ) : null}

      <MembersTable
        members={members}
        working={working}
        onRevoke={(member) => void revoke(member)}
        onLevel={(member, next) => void changeLevel(member, next)}
      />

      <div className="form">
        <div className="field">
          <label className="label" htmlFor="invite-email">
            Invite by email
          </label>
          <input
            id="invite-email"
            type="text"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="invite-name">
            Name (optional)
          </label>
          <input
            id="invite-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="invite-level">
            Access
          </label>
          <Select
            id="invite-level"
            value={level}
            options={LEVEL_OPTIONS}
            disabled={working !== null}
            onChange={(next) => setLevel(next as MembershipLevel)}
          />
          <div className="sub">{MEMBERSHIP_LEVEL_DESCRIPTIONS[level]}</div>
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn go"
            disabled={email.trim().length === 0 || working !== null}
            onClick={() => void invite()}
          >
            {working === 'invite' ? 'Inviting…' : 'Invite'}
          </button>
        </div>
        <div className="admin-note">
          Creates the account and membership; no email is sent. Revoke removes the membership only.
          Access can be changed afterwards from the table above; re-inviting somebody does not
          change it.
        </div>
      </div>
    </div>
  );
}
